import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { analytics } from 'react-shisell';
import { LocationDescriptor } from 'history';
import { compose, withStateHandlers } from 'recompose';
import { withTweekKeys } from 'react-tweek';
import { Route, Switch, Redirect, RouteProps, RouteComponentProps } from 'react-router-dom';
import Footer, { FooterTypeCode } from './components/Footer';
import Headered from './components/Headered';
import bootstrapCssUrl from '!file-loader!bootstrap/dist/css/bootstrap.min.css';
import expertSessionsCssUrl from '!file-loader?name=[hash].css!extract-loader!css-loader!sass-loader!./pages/experiences/main.scss';
import { UserProvider } from './pages/auth/UserProvider';
import { PartnerThemeProvider, theme } from './themes';
import routes, { internalRedirects, externalRedirects } from './routes';

import styled from 'styled-components';
import { default as ExpertChat } from './components/ExpertChat';
import PageTitle from './components/PageTitle';
import { useQueryParamsAsCookies, useQueryParam } from './hooks';
import { QueryParamName, CookieName } from './constants';
import { getRelativeDateInDays, validateTestGroup, getCommonPagePropsForAnalytics, updateTestGroup } from './utils';
import withQueryParamAnalytics from 'packages/web-app/src/withQueryParamAnalytics';
import { withScroll } from './components/ScrollIntoView';
import { PartnerProvider } from 'packages/smart-home-support/partner';
import { TestGroup } from './models';

export type FooterConfig = {
  type: FooterTypeCode;
};

export type HeaderConfig = {
  isLogoLinked?: boolean;
  isSticky?: boolean;
  isNavigable?: boolean;
  isMenuOpen?: boolean;
  hasSignupButton?: boolean;
  hideExpertChat?: boolean;
  backTo?: LocationDescriptor;
};

export type AppProps = {
  headerConfig: HeaderConfig;
  footerType: FooterTypeCode;
  isAgent: boolean;
  toggleMenu: Function;
  closeMenu: Function;
  setIsHeaderSticky: Function;
  setIsHeaderNavigable: Function;
  setIsHeaderLogoLinked: Function;
  setIsHiddenFooter: Function;
  setHasSignupButton: Function;
  setHideExpertChat: Function;
  setBackTo: (backTo?: LocationDescriptor) => void;
  setIsAgent: Function;
  setFooterType: (typeCode: FooterTypeCode) => void;
  pageScrollPosition: number;
  isAuthEnabled: boolean;
};

const externalRedirect = (location: string) => {
  window.location.href = location;
  return null;
};

const Container = styled.div`
  flex: 1;
`;

const AppHelmet: React.SFC = () => (
  <Helmet defer={false}>
    <link rel="prefetch" href={bootstrapCssUrl} />
    <link rel="prefetch" href={expertSessionsCssUrl} />
    <link rel="preload" href={bootstrapCssUrl} as="style" />
    <link rel="preload" href={expertSessionsCssUrl} as="style" />
  </Helmet>
);

const App: React.SFC<AppProps & RouteProps & RouteComponentProps<string>> = (props) => {
  /**
   * Gather experiments for User
   */

  const [t_whatscovered_active] = useQueryParam<string>(TestGroup.WhatsCovered_Redesign, (value) => value as string);
  const [t_warranty_active] = useQueryParam<string>(TestGroup.Warranty_Redesign, (value) => value as string);

  useEffect(() => {
    if (t_whatscovered_active) {
      updateTestGroup(TestGroup.WhatsCovered_Redesign, t_whatscovered_active);
    }

    if (t_warranty_active) {
      updateTestGroup(TestGroup.Warranty_Redesign, t_warranty_active);
    }
  }, [t_whatscovered_active, t_warranty_active]);

  /**
   * Set Cookies for important identities and codes
   */

  const cookieExpiration = t_whatscovered_active || t_warranty_active ? 31 : 7;

  useQueryParamsAsCookies(
    [
      {
        queryParamName: QueryParamName.ReferrerCode,
        cookieName: CookieName.ReferrerCode,
        validate: () => true,
        getExpirationDate: getRelativeDateInDays(cookieExpiration),
      },
      {
        queryParamName: QueryParamName.VendorData,
        cookieName: CookieName.VendorData,
        validate: () => true,
        getExpirationDate: getRelativeDateInDays(cookieExpiration),
      },
      {
        queryParamName: QueryParamName.TestGroup,
        cookieName: CookieName.TestGroup,
        validate: validateTestGroup,
      },
      {
        queryParamName: QueryParamName.AgentId,
        cookieName: CookieName.AgentId,
        validate: () => true,
        getExpirationDate: getRelativeDateInDays(cookieExpiration),
        analyticName: 'agentId',
      },
      {
        queryParamName: QueryParamName.SaleSessionId,
        cookieName: CookieName.SaleSessionId,
        validate: () => true,
        getExpirationDate: getRelativeDateInDays(cookieExpiration),
        analyticName: 'saleSessionId',
      },
      {
        queryParamName: QueryParamName.StoreCode,
        cookieName: CookieName.StoreCode,
        validate: () => true,
        getExpirationDate: getRelativeDateInDays(cookieExpiration),
        analyticName: 'storeCode',
      },
      {
        queryParamName: QueryParamName.CampaignId,
        cookieName: CookieName.CampaignId,
        validate: () => true,
        getExpirationDate: getRelativeDateInDays(cookieExpiration),
        analyticName: QueryParamName.CampaignId,
      },
      {
        queryParamName: QueryParamName.AccountId,
        cookieName: CookieName.AccountId,
        validate: () => true,
        getExpirationDate: getRelativeDateInDays(cookieExpiration),
        analyticName: QueryParamName.AccountId,
      },
      {
        queryParamName: QueryParamName.SourceApp,
        cookieName: CookieName.SourceApp,
        validate: () => true,
        getExpirationDate: getRelativeDateInDays(cookieExpiration),
        analyticName: QueryParamName.SourceApp,
      },
      {
        queryParamName: TestGroup.WhatsCovered_Redesign,
        cookieName: TestGroup.WhatsCovered_Redesign,
        validate: () => true,
        getExpirationDate: getRelativeDateInDays(cookieExpiration),
        analyticName: TestGroup.WhatsCovered_Redesign,
      },
      {
        queryParamName: TestGroup.Warranty_Redesign,
        cookieName: TestGroup.Warranty_Redesign,
        validate: () => true,
        getExpirationDate: getRelativeDateInDays(cookieExpiration),
        analyticName: TestGroup.Warranty_Redesign,
      },
      {
        queryParamName: QueryParamName.CommunicationId,
        cookieName: CookieName.CommunicationId,
        validate: () => true,
        getExpirationDate: getRelativeDateInDays(cookieExpiration),
        analyticName: QueryParamName.CommunicationId,
      },
    ],
    props.history
  );

  analytics.transformDispatcher((dispatcher) => dispatcher.withExtras(getCommonPagePropsForAnalytics(props)));

  return (
    <UserProvider isAuthEnabled={props.isAuthEnabled}>
      <Switch>
        {externalRedirects.map(({ route, url }, i) => (
          <Route {...route} key={i} render={() => externalRedirect(url)} />
        ))}
        {internalRedirects.map(({ route, toPath }, i) => (
          <Redirect {...route} key={i} to={toPath} />
        ))}
        <PartnerThemeProvider theme={theme}>
          <PartnerProvider>
            <AppHelmet />
            <ExpertChat theme={theme} disabled={props.headerConfig.hideExpertChat === true} />
            <Headered
              isSticky={props.headerConfig.isSticky !== false}
              isNavigable={props.headerConfig.isNavigable !== false}
              isLogoLinked={props.isAgent ? false : props.headerConfig.isLogoLinked !== false}
              isMenuOpen={props.headerConfig.isMenuOpen !== false}
              hasSignupButton={props.headerConfig.hasSignupButton !== false}
              backTo={props.headerConfig.backTo}
              toggleMenu={props.toggleMenu}
              closeMenu={props.closeMenu}
              path={(props.location && props.location.pathname) || '/'}
              isAuthEnabled={props.isAuthEnabled}
            >
              <Container>
                <Switch>
                  {routes.map(({ route, Component, title }, i) => (
                    <Route
                      {...route}
                      key={i}
                      render={(routeProps) => (
                        <>
                          <PageTitle title={title} />
                          <Component {...props} {...routeProps} />
                        </>
                      )}
                    />
                  ))}
                </Switch>
              </Container>
              <Footer
                type={props.footerType}
                isNavigable={!props.isAgent}
                pathname={props.location ? props.location.pathname : ''}
                isAuthEnabled={props.isAuthEnabled}
              />
            </Headered>
          </PartnerProvider>
        </PartnerThemeProvider>
      </Switch>
    </UserProvider>
  );
};

export default compose<AppProps, any>(
  withTweekKeys(
    {
      isAuthEnabled: 'soluto_home_web/smart_home_support/auth/is_enabled',
    },
    {
      defaultValues: {
        isAuthEnabled: undefined,
      },
    }
  ),
  withStateHandlers(
    {
      headerConfig: { isMenuOpen: false, isSticky: true, isNavigable: true, isLogoLinked: true, hasSignupButton: true },
      footerConfig: { isMinimal: undefined, isHidden: false },
      isAgent: false,
      footerType: 'branded',
    },
    {
      setFooterType: () => (type: FooterTypeCode) => ({ footerType: type }),
      closeMenu: ({ headerConfig }) => () => ({
        headerConfig: { ...headerConfig, isMenuOpen: false },
      }),
      toggleMenu: ({ headerConfig }) => () => {
        if (headerConfig.isSticky === false) {
          window.scrollTo(0, 0);
        }
        return { headerConfig: { ...headerConfig, isMenuOpen: !headerConfig.isMenuOpen } };
      },
      setIsHeaderSticky: ({ headerConfig }) => (isSticky) => ({
        headerConfig: { ...headerConfig, isSticky },
      }),
      setIsHeaderNavigable: ({ headerConfig }) => (isNavigable) => ({
        headerConfig: { ...headerConfig, isNavigable },
      }),
      setIsHeaderLogoLinked: ({ headerConfig }) => (isLogoLinked) => ({
        headerConfig: { ...headerConfig, isLogoLinked },
      }),
      setIsHiddenFooter: ({ footerConfig }) => (isHidden) => ({
        footerConfig: { ...footerConfig, isHidden },
      }),
      setHideExpertChat: ({ headerConfig }) => (hideExpertChat) => ({
        headerConfig: { ...headerConfig, hideExpertChat },
      }),
      setHasSignupButton: ({ headerConfig }) => (hasSignupButton) => ({
        headerConfig: { ...headerConfig, hasSignupButton },
      }),
      setBackTo: ({ headerConfig }) => (backTo) => ({
        headerConfig: { ...headerConfig, backTo },
      }),
      setIsAgent: () => (isAgent) => ({
        isAgent,
      }),
    }
  ),
  withQueryParamAnalytics(
    {
      analyticName: 'agentId',
      analyticType: 'extraData',
      queryParamName: QueryParamName.AgentId,
      cookiesName: CookieName.AgentId,
    },
    {
      analyticName: 'salesSessionId',
      analyticType: 'extraData',
      queryParamName: QueryParamName.SaleSessionId,
      cookiesName: CookieName.SaleSessionId,
    },
    {
      analyticName: 'storeCode',
      analyticType: 'extraData',
      queryParamName: QueryParamName.StoreCode,
      cookiesName: CookieName.StoreCode,
    },
    {
      analyticName: QueryParamName.CampaignId,
      analyticType: 'extraData',
      queryParamName: QueryParamName.CampaignId,
      cookiesName: CookieName.CampaignId,
    },
    {
      analyticName: 'accountId',
      analyticType: 'extraData',
      queryParamName: QueryParamName.AccountId,
      cookiesName: CookieName.AccountId,
    },
    {
      analyticName: TestGroup.WhatsCovered_Redesign,
      analyticType: 'extraData',
      queryParamName: TestGroup.WhatsCovered_Redesign,
      cookiesName: TestGroup.WhatsCovered_Redesign,
    },
    {
      analyticName: TestGroup.Warranty_Redesign,
      analyticType: 'extraData',
      queryParamName: TestGroup.Warranty_Redesign,
      cookiesName: TestGroup.Warranty_Redesign,
    },
    {
      analyticName: 'MetaData.OfferSessionId',
      analyticType: 'extraData',
      queryParamName: QueryParamName.SaleSessionId,
      cookiesName: CookieName.SaleSessionId,
    }
  ),
  withScroll({
    offset: -40,
  })
)(App);



// WEBPACK FOOTER //
// ./packages/smart-home-support/App.tsx